import { useCurrentUser } from "lib/store/use-current-user"
import { Box, Button, ListItem, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { AdoptClient, FlushGraphQLCache } from "event-definitions"

export function NoLicenseShareLinkItem({ link, onSelect }) {
    const { metadata: user } = useCurrentUser() || {}
    const companyName = link?.contact?.companyName || "Unknown Organisation"
    const displayName = link?.contact?.displayName || ""
    const orgLabel = displayName ? `${companyName} (${displayName})` : companyName
    const email = link?.contact?.email

    const userName = user?.displayName || ""
    const userEmail = user?.email ? `\n${user.email}` : ""

    const subject = encodeURIComponent("Enquiry regarding access via ShareLink without a Licence")
    const body = encodeURIComponent(
        `Hello,\n\nI received a valid SFG20 share link (${link.name}) from ${orgLabel} but do not have an active Licence. Please advise on how I can gain access.\n\nRegards,\n${userName}${userEmail}`
    )

    const mailto = email ? `mailto:${email}?subject=${subject}&body=${body}` : undefined
    if (!user) return null
    if (link?.canViewWithoutLicense) {
        return (
            <ListItem
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    my: 1,
                    borderRadius: 3,
                    backgroundColor: "background.neutral",
                    border: "2px solid",
                    borderColor: "primary.light",
                    minHeight: 110,
                    px: 3,
                    py: 2,
                }}
                data-cy="unlicensed-sharelink-open"
                disableGutters
            >
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Iconify
                        icon="mdi:link-variant"
                        width={22}
                        height={22}
                        color="#38939B"
                        style={{ marginRight: 8 }}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                        {link.name}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                    This content was shared with you by {orgLabel}. You can open it without buying a licence.
                </Typography>
                <Button variant="contained" onClick={openShareLink} data-cy="open-share-link-without-license">
                    Open shared schedules
                </Button>
            </ListItem>
        )
    }
    return (
        <ListItem
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                my: 1,
                borderRadius: 3,
                backgroundColor: "#FFF8E1",
                border: "2px dashed #FFC107",
                minHeight: 110,
                px: 3,
                py: 2,
            }}
            data-cy="unlicensed-sharelink"
            disableGutters
        >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Iconify
                    icon="mdi:alert-circle-outline"
                    width={22}
                    height={22}
                    color="#FFA000"
                    style={{ marginRight: 8 }}
                />
                <Typography variant="subtitle2" fontWeight={700}>
                    {link.name}
                </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                This content was shared with you by {orgLabel}. Please contact the organisation that shared this link
                with you, or contact SFG20 Customer Services to discuss access options.
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    mt: 1,
                    width: "100%",
                    flexWrap: "wrap",
                    justifyContent: "flex-start",
                }}
            >
                <Button
                    variant="contained"
                    component="a"
                    href={mailto}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!email}
                    sx={{
                        flex: "1 1 0",
                        minWidth: 170,
                        whiteSpace: "nowrap",
                    }}
                    data-cy="contact-issuer"
                    color="primary"
                >
                    Contact {companyName}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleRowClick}
                    sx={{
                        flex: "1 1 0",
                        minWidth: 150,
                        whiteSpace: "nowrap",
                    }}
                    data-cy="contact-sfg20"
                >
                    Contact SFG20
                </Button>
            </Box>
        </ListItem>
    )

    function handleRowClick() {
        if (onSelect) onSelect({ target: { value: link.name } })
    }

    function openShareLink() {
        FlushGraphQLCache.raise()
        AdoptClient.raise(undefined)
        window.location.href = link.url
    }
}
